import { format } from 'date-fns';
import {
  ArrowLeft,
  Copy,
  History,
  Loader2,
  Lock,
  MapPin,
  Save,
  Trash2,
  User,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useToast } from '../../hooks/useToast';
import manifestService, {
  Manifest,
  ManifestHistoryEntry,
  ManifestStatus,
} from '../../services/manifestService';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Textarea } from '../ui/textarea';
import AWBSearch from './AWBSearch';
import BillingTab from './BillingTab';
import TrackingTab from './TrackingTab';

export interface ManifestDetailProps {
  manifestId: string;
  onBack: () => void;
  onUpdate?: () => void;
}

interface DropdownOption {
  value: string;
  label: string;
  code?: string;
}

const ManifestDetail: React.FC<ManifestDetailProps> = ({
  manifestId,
  onBack,
  onUpdate,
}) => {
  const [manifest, setManifest] = useState<Manifest | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('entry');
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<ManifestHistoryEntry[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Master data states
  const [vendors, setVendors] = useState<DropdownOption[]>([]);
  const [hubs, setHubs] = useState<DropdownOption[]>([]);
  const [companies, setCompanies] = useState<DropdownOption[]>([]);
  const [services, setServices] = useState<DropdownOption[]>([]);
  const [licenses, setLicenses] = useState<DropdownOption[]>([]);

  // Form state
  const [formData, setFormData] = useState<any>({});

  const { toast } = useToast();

  // Fetch manifest data
  const fetchManifest = async () => {
    try {
      setLoading(true);
      const data = await manifestService.getManifest(manifestId);
      setManifest(data);
      setFormData({
        date: data.date ? format(new Date(data.date), 'yyyy-MM-dd') : '',
        time: data.time || '',
        forwarder: data.forwarder?._id || '',
        service: data.service?._id || '',
        vendor: data.vendor?._id || '',
        masterNo: data.masterNo || '',
        masterEdiBagNo: data.masterEdiBagNo || '',
        bagNoRange: data.bagNoRange || '',
        remark: data.remark || '',
        runNo: data.runNo || '',
        flightNo: data.flightNo || '',
        vendorCdNo: data.vendorCdNo || '',
        noOfBags: data.noOfBags || 1,
        arrivalDate: data.arrivalDate
          ? format(new Date(data.arrivalDate), 'yyyy-MM-dd')
          : '',
        arrivalTime: data.arrivalTime || '',
        company: data.company?._id || '',
        license: data.license?._id || '',
        originHub: data.originHub?._id || '',
        destinationHub: data.destinationHub?._id || '',
        manifestType: data.manifestType || 'EXPORT',
      });
    } catch (error) {
      console.error('Error fetching manifest:', error);
      toast({
        title: 'Error',
        description: 'Failed to load manifest',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch master data
  const fetchMasterData = async () => {
    try {
      const API_BASE_URL =
        import.meta.env.VITE_API_URL || 'https://awbbackend.bliitz.in';
      const token = localStorage.getItem('accessToken');
      const headers = { Authorization: `Bearer ${token}` };

      const [vendorsRes, hubsRes, companiesRes, servicesRes, licensesRes] =
        await Promise.all([
          fetch(`${API_BASE_URL}/api/master-data/vendor/dropdown`, {
            headers,
          }).then(r => r.json()),
          fetch(`${API_BASE_URL}/api/master-data/hub/dropdown`, {
            headers,
          }).then(r => r.json()),
          fetch(`${API_BASE_URL}/api/master-data/company/dropdown`, {
            headers,
          }).then(r => r.json()),
          fetch(`${API_BASE_URL}/api/master-data/service/dropdown`, {
            headers,
          }).then(r => r.json()),
          fetch(`${API_BASE_URL}/api/master-data/license/dropdown`, {
            headers,
          }).then(r => r.json()),
        ]);

      setVendors(vendorsRes.data || []);
      setHubs(hubsRes.data || []);
      setCompanies(companiesRes.data || []);
      setServices(servicesRes.data || []);
      setLicenses(licensesRes.data || []);
    } catch (error) {
      console.error('Error fetching master data:', error);
    }
  };

  useEffect(() => {
    fetchManifest();
    fetchMasterData();
  }, [manifestId]);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await manifestService.updateManifest(manifestId, formData);
      toast({
        title: 'Success',
        description: 'Manifest updated successfully',
      });
      fetchManifest();
      onUpdate?.();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update manifest',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAddAWBs = async (awbNumbers: string[]) => {
    await manifestService.addAWBs(manifestId, {
      awbNumbers,
      trackBy: 'AWB_NUMBER',
    });
    fetchManifest();
    onUpdate?.();
  };

  const handleRemoveAWB = async (awbNumber: string) => {
    try {
      await manifestService.removeAWB(manifestId, awbNumber);
      toast({
        title: 'Success',
        description: 'AWB removed from manifest',
      });
      fetchManifest();
      onUpdate?.();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to remove AWB',
        variant: 'destructive',
      });
    }
  };

  const handleLockManifest = async () => {
    try {
      await manifestService.lockManifest(manifestId);
      toast({
        title: 'Success',
        description: 'Manifest locked successfully',
      });
      fetchManifest();
      onUpdate?.();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to lock manifest',
        variant: 'destructive',
      });
    }
  };

  const handleCloneManifest = async () => {
    try {
      const clonedManifest = await manifestService.cloneManifest(manifestId);
      toast({
        title: 'Success',
        description: `Manifest cloned as ${clonedManifest.manifestNo}`,
      });
      onUpdate?.();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to clone manifest',
        variant: 'destructive',
      });
    }
  };

  const handleViewHistory = async () => {
    setShowHistory(true);
    setLoadingHistory(true);
    try {
      const historyData = await manifestService.getHistory(manifestId);
      setHistory(historyData);
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleDownloadReport = async () => {
    try {
      const blob = await manifestService.downloadReport(manifestId, 'excel');
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `manifest_${manifest?.manifestNo}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to download report',
        variant: 'destructive',
      });
    }
  };

  const getStatusBadge = (status: ManifestStatus) => {
    const statusConfig = {
      DRAFT: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      LOCKED: 'bg-blue-100 text-blue-800 border-blue-200',
      IN_TRANSIT: 'bg-purple-100 text-purple-800 border-purple-200',
      DELIVERED: 'bg-green-100 text-green-800 border-green-200',
    };
    return (
      <Badge className={statusConfig[status] || 'bg-gray-100 text-gray-800'}>
        {status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center h-96'>
        <Loader2 className='h-8 w-8 animate-spin' />
        <span className='ml-2'>Loading manifest...</span>
      </div>
    );
  }

  if (!manifest) {
    return (
      <div className='flex flex-col items-center justify-center h-96'>
        <p className='text-muted-foreground'>Manifest not found</p>
        <Button onClick={onBack} className='mt-4'>
          Go Back
        </Button>
      </div>
    );
  }

  const isLocked = manifest.status !== 'DRAFT';

  return (
    <div className='space-y-4'>
      {/* Header */}
      <div className='flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between'>
        <div className='flex items-center gap-2 sm:gap-4'>
          <Button variant='outline' onClick={onBack} size='sm' className='sm:size-default'>
            <ArrowLeft className='h-4 w-4 sm:mr-2' />
            <span className='hidden sm:inline'>BACK</span>
          </Button>
          <h1 className='text-lg sm:text-2xl font-bold'>MANIFEST</h1>
        </div>
        <div className='flex flex-wrap items-center gap-2'>
          <Button variant='outline' size='sm' className='flex-1 sm:flex-none' onClick={handleViewHistory}>
            <History className='h-4 w-4 sm:mr-2' />
            <span className='hidden sm:inline'>MANIFEST </span>HISTORY
          </Button>
          <Button variant='outline' size='sm' className='flex-1 sm:flex-none' onClick={handleCloneManifest}>
            <Copy className='h-4 w-4 sm:mr-2' />
            <span className='hidden sm:inline'>CLONE </span>CLONE
          </Button>
          <Button
            variant='destructive'
            size='sm'
            className='flex-1 sm:flex-none'
            onClick={handleLockManifest}
            disabled={isLocked}
          >
            <Lock className='h-4 w-4 sm:mr-2' />
            <span className='hidden sm:inline'>LOCK </span>LOCK
          </Button>
        </div>
      </div>

      {/* Manifest Detail Card */}
      <div className='bg-background border rounded-lg p-4 sm:p-6'>
        <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6'>
          <h2 className='text-lg sm:text-xl font-semibold'>MANIFEST DETAIL</h2>
          <div className='flex items-center gap-2'>
            <span className='text-sm text-muted-foreground'>Status:</span>
            {getStatusBadge(manifest.status)}
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className='flex w-full h-auto flex-wrap gap-1 sm:grid sm:grid-cols-3'>
            <TabsTrigger
              value='entry'
              className='flex-1 sm:flex-none flex items-center justify-center gap-1 sm:gap-2 text-xs sm:text-sm py-2 data-[state=active]:bg-yellow-500 data-[state=active]:text-black'
            >
              <User className='h-3 w-3 sm:h-4 sm:w-4' />
              ENTRY
            </TabsTrigger>
            <TabsTrigger
              value='tracking'
              className='flex-1 sm:flex-none flex items-center justify-center gap-1 sm:gap-2 text-xs sm:text-sm py-2 data-[state=active]:bg-green-500 data-[state=active]:text-white'
            >
              <MapPin className='h-3 w-3 sm:h-4 sm:w-4' />
              <span className='hidden sm:inline'>ADD </span>TRACKING<span className='hidden sm:inline'> EVENT TO AWBS</span>
            </TabsTrigger>
            <TabsTrigger
              value='billing'
              className='flex-1 sm:flex-none flex items-center justify-center gap-1 sm:gap-2 text-xs sm:text-sm py-2 data-[state=active]:bg-yellow-500 data-[state=active]:text-black'
            >
              📄 BILLING
            </TabsTrigger>
          </TabsList>

          {/* Entry Tab */}
          <TabsContent value='entry' className='mt-4 sm:mt-6'>
            <div className='flex justify-end gap-2 mb-4'>
              <Button
                onClick={handleSave}
                disabled={isLocked || saving}
                className='bg-green-600 hover:bg-green-700 w-full sm:w-auto'
              >
                {saving ? (
                  <Loader2 className='h-4 w-4 animate-spin mr-2' />
                ) : (
                  <Save className='h-4 w-4 mr-2' />
                )}
                UPDATE MANIFEST
              </Button>
            </div>

            {/* Form Grid */}
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-x-8 lg:gap-y-4'>
              {/* Left Column */}
              <div className='space-y-4'>
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                  <div>
                    <Label>MANIFEST NO.</Label>
                    <Input
                      value={manifest.manifestNo}
                      disabled
                      className='bg-muted'
                    />
                  </div>
                  <div>
                    <Label>DATE *</Label>
                    <Input
                      type='date'
                      value={formData.date}
                      onChange={e => handleInputChange('date', e.target.value)}
                      disabled={isLocked}
                    />
                  </div>
                </div>

                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                  <div>
                    <Label>FORWARDER</Label>
                    <Select
                      value={formData.forwarder}
                      onValueChange={value =>
                        handleInputChange('forwarder', value)
                      }
                      disabled={isLocked}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder='Select...' />
                      </SelectTrigger>
                      <SelectContent>
                        {companies.map(c => (
                          <SelectItem key={c.value} value={c.value}>
                            {c.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>TIME *</Label>
                    <Input
                      type='time'
                      value={formData.time}
                      onChange={e => handleInputChange('time', e.target.value)}
                      disabled={isLocked}
                    />
                  </div>
                </div>

                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                  <div>
                    <Label>SERVICE</Label>
                    <Select
                      value={formData.service}
                      onValueChange={value =>
                        handleInputChange('service', value)
                      }
                      disabled={isLocked}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder='Select...' />
                      </SelectTrigger>
                      <SelectContent>
                        {services.map(s => (
                          <SelectItem key={s.value} value={s.value}>
                            {s.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>RUN NUMBER</Label>
                    <Input
                      value={formData.runNo}
                      onChange={e => handleInputChange('runNo', e.target.value)}
                      disabled={isLocked}
                      placeholder='ALPHABET-NUMBER'
                    />
                  </div>
                </div>

                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                  <div>
                    <Label>VENDOR *</Label>
                    <Select
                      value={formData.vendor}
                      onValueChange={value =>
                        handleInputChange('vendor', value)
                      }
                      disabled={isLocked}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder='Select...' />
                      </SelectTrigger>
                      <SelectContent>
                        {vendors.map(v => (
                          <SelectItem key={v.value} value={v.value}>
                            {v.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>FLIGHT NO</Label>
                    <Input
                      value={formData.flightNo}
                      onChange={e =>
                        handleInputChange('flightNo', e.target.value)
                      }
                      disabled={isLocked}
                    />
                  </div>
                </div>

                <div>
                  <Label>MASTER NO.</Label>
                  <Input
                    value={formData.masterNo}
                    onChange={e =>
                      handleInputChange('masterNo', e.target.value)
                    }
                    disabled={isLocked}
                  />
                </div>

                <div>
                  <Label>MASTER EDI BAG NO</Label>
                  <Input
                    value={formData.masterEdiBagNo}
                    onChange={e =>
                      handleInputChange('masterEdiBagNo', e.target.value)
                    }
                    disabled={isLocked}
                  />
                </div>

                <div>
                  <Label>BAG NO. RANGE</Label>
                  <Input
                    value={formData.bagNoRange}
                    onChange={e =>
                      handleInputChange('bagNoRange', e.target.value)
                    }
                    disabled={isLocked}
                  />
                </div>

                <div>
                  <Label>REMARK</Label>
                  <Textarea
                    value={formData.remark}
                    onChange={e => handleInputChange('remark', e.target.value)}
                    disabled={isLocked}
                    rows={3}
                  />
                </div>

                <div>
                  <Label>CREATED BY USER</Label>
                  <Input
                    value={manifest.createdByUser?.name || ''}
                    disabled
                    className='bg-muted'
                  />
                </div>
              </div>

              {/* Right Column */}
              <div className='space-y-4'>
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                  <div>
                    <Label>VENDOR CD NO.</Label>
                    <Input
                      value={formData.vendorCdNo}
                      onChange={e =>
                        handleInputChange('vendorCdNo', e.target.value)
                      }
                      disabled={isLocked}
                    />
                  </div>
                  <div>
                    <Label>ORIGIN HUB *</Label>
                    <Select
                      value={formData.originHub}
                      onValueChange={value =>
                        handleInputChange('originHub', value)
                      }
                      disabled={isLocked}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder='Select...' />
                      </SelectTrigger>
                      <SelectContent>
                        {hubs.map(h => (
                          <SelectItem key={h.value} value={h.value}>
                            {h.code ? `${h.code} - ${h.label}` : h.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                  <div>
                    <Label>NO. OF BAGS</Label>
                    <Input
                      type='number'
                      value={formData.noOfBags}
                      onChange={e =>
                        handleInputChange(
                          'noOfBags',
                          parseInt(e.target.value) || 1
                        )
                      }
                      disabled={isLocked}
                    />
                  </div>
                  <div>
                    <Label>DESTINATION HUB</Label>
                    <Select
                      value={formData.destinationHub}
                      onValueChange={value =>
                        handleInputChange('destinationHub', value)
                      }
                      disabled={isLocked}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder='Select...' />
                      </SelectTrigger>
                      <SelectContent>
                        {hubs.map(h => (
                          <SelectItem key={h.value} value={h.value}>
                            {h.code ? `${h.code} - ${h.label}` : h.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                  <div>
                    <Label>ARRIVAL DATE</Label>
                    <Input
                      type='date'
                      value={formData.arrivalDate}
                      onChange={e =>
                        handleInputChange('arrivalDate', e.target.value)
                      }
                      disabled={isLocked}
                    />
                  </div>
                  <div>
                    <Label>ARRIVAL TIME</Label>
                    <Input
                      type='time'
                      value={formData.arrivalTime}
                      onChange={e =>
                        handleInputChange('arrivalTime', e.target.value)
                      }
                      disabled={isLocked}
                    />
                  </div>
                </div>

                <div>
                  <Label>COMPANY</Label>
                  <Select
                    value={formData.company}
                    onValueChange={value => handleInputChange('company', value)}
                    disabled={isLocked}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder='SELECT COMPANY...' />
                    </SelectTrigger>
                    <SelectContent>
                      {companies.map(c => (
                        <SelectItem key={c.value} value={c.value}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>LICENSE</Label>
                  <Select
                    value={formData.license}
                    onValueChange={value => handleInputChange('license', value)}
                    disabled={isLocked}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder='SELECT LICENSE...' />
                    </SelectTrigger>
                    <SelectContent>
                      {licenses.map(l => (
                        <SelectItem key={l.value} value={l.value}>
                          {l.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Calculated Fields */}
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                  <div>
                    <Label>TOTAL ACTUAL WT</Label>
                    <Input
                      value={manifest.totalActualWeight.toFixed(2)}
                      disabled
                      className='bg-muted'
                    />
                  </div>
                  <div>
                    <Label>CD IMAGE</Label>
                    <Input type='file' disabled={isLocked} />
                  </div>
                </div>

                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                  <div>
                    <Label>TOTAL VOLUMETRIC WT</Label>
                    <Input
                      value={manifest.totalVolumetricWeight.toFixed(2)}
                      disabled
                      className='bg-muted'
                    />
                  </div>
                  <div>
                    <Label>MANIFEST TYPE *</Label>
                    <Select
                      value={formData.manifestType}
                      onValueChange={value =>
                        handleInputChange('manifestType', value)
                      }
                      disabled={isLocked}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='EXPORT'>EXPORT</SelectItem>
                        <SelectItem value='IMPORT'>IMPORT</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label>TOTAL CHARGEABLE WT</Label>
                  <Input
                    value={manifest.totalChargeableWeight.toFixed(2)}
                    disabled
                    className='bg-muted'
                  />
                </div>

                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                  <div>
                    <Label>NO. OF AWB</Label>
                    <Input
                      value={manifest.noOfAwb}
                      disabled
                      className='bg-muted'
                    />
                  </div>
                  <div>
                    <Label>NO. OF PCS</Label>
                    <Input
                      value={manifest.noOfPcs}
                      disabled
                      className='bg-muted'
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* AWB Section */}
            <div className='mt-6 sm:mt-8 border-t pt-4 sm:pt-6'>
              {/* AWB Search */}
              {!isLocked && (
                <div className='mb-4 sm:mb-6'>
                  <h3 className='text-base sm:text-lg font-semibold mb-3 sm:mb-4'>
                    Search and Add AWBs
                  </h3>
                  <AWBSearch onAddAWBs={handleAddAWBs} disabled={isLocked} />
                </div>
              )}

              {/* AWB Table */}
              <div className='flex flex-wrap items-center gap-2 mb-4'>
                <Button
                  variant='outline'
                  size='sm'
                  className='bg-blue-600 text-white hover:bg-blue-700 text-xs sm:text-sm'
                >
                  SEARCH AWB
                </Button>
                <Button
                  variant='outline'
                  size='sm'
                  className='bg-green-600 text-white hover:bg-green-700 text-xs sm:text-sm'
                >
                  UPDATE MANIFEST
                </Button>
                <Button
                  variant='outline'
                  size='sm'
                  className='bg-blue-500 text-white hover:bg-blue-600 text-xs sm:text-sm'
                >
                  BAGGING
                </Button>
              </div>

              <div className='overflow-x-auto -mx-4 sm:mx-0'>
              <div className='min-w-[900px] sm:min-w-0 px-4 sm:px-0'>
              <Table>
                <TableHeader>
                  <TableRow className='bg-blue-600 text-white'>
                    <TableHead className='text-white'>
                      <Checkbox disabled />
                    </TableHead>
                    <TableHead className='text-white'>BAG NO.</TableHead>
                    <TableHead className='text-white'>EDI BAG NO.</TableHead>
                    <TableHead className='text-white'>BAG ID</TableHead>
                    <TableHead className='text-white'>TRACK BY</TableHead>
                    <TableHead className='text-white'>AWB NO.</TableHead>
                    <TableHead className='text-white'>ACTUAL AWB NO.</TableHead>
                    <TableHead className='text-white'>FORWARDER NO.</TableHead>
                    <TableHead className='text-white'>BOOKING DATE</TableHead>
                    <TableHead className='text-white'>WEIGHT</TableHead>
                    <TableHead className='text-white'>PCS.</TableHead>
                    <TableHead className='text-white'>ACTION DUTY</TableHead>
                    <TableHead className='text-white'>NSL 1</TableHead>
                    <TableHead className='text-white'>IGSTN</TableHead>
                    <TableHead className='text-white'>ACTION</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {manifest.awbs.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={15}
                        className='text-center py-8 text-muted-foreground'
                      >
                        No AWBs added to this manifest
                      </TableCell>
                    </TableRow>
                  ) : (
                    manifest.awbs.map((awb, index) => (
                      <TableRow key={awb.awbNo}>
                        <TableCell>
                          <Checkbox />
                        </TableCell>
                        <TableCell>{awb.bagNo || '-'}</TableCell>
                        <TableCell>{awb.ediBagNo || '-'}</TableCell>
                        <TableCell>{awb.bagId || '-'}</TableCell>
                        <TableCell>{awb.trackBy}</TableCell>
                        <TableCell className='font-medium'>
                          {awb.awbNo}
                        </TableCell>
                        <TableCell>{awb.actualAwbNo || awb.awbNo}</TableCell>
                        <TableCell>{awb.forwardingNo || '-'}</TableCell>
                        <TableCell>
                          {awb.bookingDate
                            ? format(new Date(awb.bookingDate), 'dd/MM/yyyy')
                            : '-'}
                        </TableCell>
                        <TableCell>{awb.weight}</TableCell>
                        <TableCell>{awb.pcs}</TableCell>
                        <TableCell>{awb.actionDuty ? 'Y' : 'N'}</TableCell>
                        <TableCell>{awb.nsl1 ? 'Y' : 'N'}</TableCell>
                        <TableCell>{awb.igstn ? 'Y' : 'N'}</TableCell>
                        <TableCell>
                          <div className='flex items-center gap-1'>
                            <Button
                              variant='link'
                              size='sm'
                              className='text-blue-600'
                            >
                              VIEW
                            </Button>
                            {!isLocked && (
                              <Button
                                variant='ghost'
                                size='sm'
                                className='text-red-600 hover:text-red-700'
                                onClick={() => handleRemoveAWB(awb.awbNo)}
                              >
                                <Trash2 className='h-4 w-4' />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              </div>
              </div>
            </div>
          </TabsContent>

          {/* Tracking Tab */}
          <TabsContent value='tracking' className='mt-4 sm:mt-6'>
            <TrackingTab
              manifest={manifest}
              onUpdate={fetchManifest}
              disabled={false}
            />
          </TabsContent>

          {/* Billing Tab */}
          <TabsContent value='billing' className='mt-4 sm:mt-6'>
            <BillingTab
              manifest={manifest}
              onUpdate={fetchManifest}
              disabled={false}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* History Dialog */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className='max-w-4xl max-h-[80vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle>Manifest History</DialogTitle>
            <DialogDescription>
              Audit log for manifest {manifest.manifestNo}
            </DialogDescription>
          </DialogHeader>

          {loadingHistory ? (
            <div className='flex items-center justify-center py-8'>
              <Loader2 className='h-6 w-6 animate-spin' />
              <span className='ml-2'>Loading history...</span>
            </div>
          ) : history.length === 0 ? (
            <div className='text-center py-8 text-muted-foreground'>
              No history entries found
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Action</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Performed At</TableHead>
                  <TableHead>Performed By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map(entry => (
                  <TableRow key={entry._id}>
                    <TableCell>
                      <Badge variant='outline'>{entry.action}</Badge>
                    </TableCell>
                    <TableCell>{entry.description}</TableCell>
                    <TableCell>
                      {format(
                        new Date(entry.performedAt),
                        'dd/MM/yyyy hh:mm:ss a'
                      )}
                    </TableCell>
                    <TableCell>{entry.performedBy}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManifestDetail;
